/**
 * Start Your Project - multi-step intake state machine.
 */

type StartMode = "photo" | "link" | "description";
type Panel =
 | "start"
 | "photo"
 | "link"
 | "processing"
 | "readback"
 | "details"
 | "confirm"
 | "result";

type PricingConfig = {
 project_types: Record<string, { label: string }>;
 species_cost_per_bf: Record<string, { label: string }>;
 finish_multiplier: Record<string, { label: string }>;
 timeline_options: Array<{ id: string; label: string }>;
 budget_options: Array<{ id: string; label: string }>;
};

type Analysis = {
 image_read_back: string;
 guessed_project_type: string;
 guessed_species: string;
 guessed_finish: string;
 confidence: string;
 clarifying_questions: string[];
};

type FormState = {
 name: string;
 email: string;
 phone: string;
 projectType: string;
 fixedSpace: boolean;
 widthIn: string;
 depthIn: string;
 heightIn: string;
 species: string;
 finish: string;
 timeline: string;
 budget: string;
 notes: string;
 clarifyingAnswers: string;
};

type IntakeRuntimeConfig = { apiEndpoint?: string };

const MAX_FILES = 3;
const MAX_BYTES = 10 * 1024 * 1024;
const CLEAN_WAIT_ATTEMPTS = 45;

/** Normalize browser MIME (or infer from filename) to a signed Content-Type. */
function normalizeImageType(file: File): string | null {
  const raw = (file.type || "").toLowerCase().trim();
  if (raw === "image/jpg" || raw === "image/jpeg") return "image/jpeg";
  if (raw === "image/png" || raw === "image/webp") return raw;
  if (
    raw === "image/heic" ||
    raw === "image/heif" ||
    /\.hei[cf]$/i.test(file.name)
  ) {
    return null;
  }
  const name = file.name.toLowerCase();
  if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".webp")) return "image/webp";
  return null;
}

function heicRejectedMessage(): string {
  return "HEIC photos aren’t supported yet — please export or share as JPEG or PNG.";
}

async function readJsonBody<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    if (res.status === 502 || res.status === 504) {
      throw new Error(
        "The server took too long. Please try again in a moment.",
      );
    }
    throw new Error(`Unexpected response (${res.status}). Please try again.`);
  }
}

const state = {
 panel: "start" as Panel,
 mode: null as StartMode | null,
 files: [] as File[],
 previewUrl: "" as string,
 rawKeys: [] as string[],
 cleanKeys: [] as string[],
 analysis: null as Analysis | null,
 form: blankForm(),
 pricing: null as PricingConfig | null,
 apiEndpoint: "" as string,
 apiConfigured: false,
};

function blankForm(): FormState {
 return {
 name: "",
 email: "",
 phone: "",
 projectType: "custom",
 fixedSpace: false,
 widthIn: "",
 depthIn: "",
 heightIn: "",
 species: "not_sure",
 finish: "not_sure",
 timeline: "flexible",
 budget: "flexible",
 notes: "",
 clarifyingAnswers: "",
 };
}

function $(sel: string): HTMLElement {
 const el = document.querySelector<HTMLElement>(sel);
 if (!el) throw new Error(`Missing element: ${sel}`);
 return el;
}

function setIntakeStatus(
 id: string,
 message: string,
 kind: "idle" | "error" | "success" | "loading" = "idle",
) {
 const el = $(id);
 el.textContent = message;
 el.dataset.state = kind;
}

async function loadIntakeConfig(): Promise<void> {
 const [cfgRes, pricingRes] = await Promise.all([
 fetch("config.json", { cache: "no-store" }),
 fetch("pricing-config.json", { cache: "no-store" }),
 ]);
 if (cfgRes.ok) {
 const cfg = (await cfgRes.json()) as IntakeRuntimeConfig;
 state.apiEndpoint = (cfg.apiEndpoint ?? "").replace(/\/$/, "");
 state.apiConfigured = true;
 }
 if (pricingRes.ok) {
 state.pricing = (await pricingRes.json()) as PricingConfig;
 }
}

function apiUrl(path: string): string {
 if (!state.apiConfigured) throw new Error("API is not configured yet.");
 return `${state.apiEndpoint}${path}`;
}

function showPanel(panel: Panel): void {
 state.panel = panel;
 document.querySelectorAll<HTMLElement>(".panel").forEach((el) => {
 el.dataset.active = el.dataset.panel === panel ? "true" : "false";
 });

 const progressMap: Record<Panel, string> = {
 start: "start",
 photo: "source",
 link: "source",
 processing: "source",
 readback: "readback",
 details: "details",
 confirm: "confirm",
 result: "result",
 };
 const active = progressMap[panel];
 const order = ["start", "source", "readback", "details", "confirm", "result"];
 const activeIdx = order.indexOf(active);
 document.querySelectorAll<HTMLElement>("#intake-progress li").forEach((li) => {
 const step = li.dataset.step ?? "";
 const idx = order.indexOf(step);
 li.dataset.active = step === active ? "true" : "false";
 li.dataset.done = idx >= 0 && idx < activeIdx ? "true" : "false";
 });
 window.scrollTo({ top: 0, behavior: "smooth" });
}

function fillSelect(
 selectId: string,
 options: Array<{ id: string; label: string }>,
 selected: string,
): void {
 const sel = $(selectId) as HTMLSelectElement;
 sel.innerHTML = "";
 for (const opt of options) {
 const o = document.createElement("option");
 o.value = opt.id;
 o.textContent = opt.label;
 if (opt.id === selected) o.selected = true;
 sel.appendChild(o);
 }
}

function populateSelects(): void {
 if (!state.pricing) return;
 const p = state.pricing;
 fillSelect(
 "#projectType",
 Object.entries(p.project_types).map(([id, v]) => ({ id, label: v.label })),
 state.form.projectType,
 );
 fillSelect(
 "#species",
 Object.entries(p.species_cost_per_bf).map(([id, v]) => ({ id, label: v.label })),
 state.form.species,
 );
 fillSelect(
 "#finish",
 Object.entries(p.finish_multiplier).map(([id, v]) => ({ id, label: v.label })),
 state.form.finish,
 );
 fillSelect("#timeline", p.timeline_options, state.form.timeline);
 fillSelect("#budget", p.budget_options, state.form.budget);
}

function labelFor(
 kind: "projectType" | "species" | "finish" | "timeline" | "budget",
 id: string,
): string {
 if (!state.pricing) return id;
 switch (kind) {
 case "projectType":
 return state.pricing.project_types[id]?.label ?? id;
 case "species":
 return state.pricing.species_cost_per_bf[id]?.label ?? id;
 case "finish":
 return state.pricing.finish_multiplier[id]?.label ?? id;
 case "timeline":
 return state.pricing.timeline_options.find((o) => o.id === id)?.label ?? id;
 case "budget":
 return state.pricing.budget_options.find((o) => o.id === id)?.label ?? id;
 default: {
 const _exhaustive: never = kind;
 return _exhaustive;
 }
 }
}

function setFixedSpace(fixed: boolean): void {
 state.form.fixedSpace = fixed;
 const standard = $("#space-standard");
 const fixedBtn = $("#space-fixed");
 standard.setAttribute("aria-pressed", fixed ? "false" : "true");
 fixedBtn.setAttribute("aria-pressed", fixed ? "true" : "false");
 $("#dims").dataset.open = fixed ? "true" : "false";
}

function applyAnalysisToForm(analysis: Analysis): void {
 state.form.projectType = analysis.guessed_project_type || "custom";
 state.form.species = analysis.guessed_species || "not_sure";
 state.form.finish = analysis.guessed_finish || "not_sure";
 populateSelects();

 const q = analysis.clarifying_questions ?? [];
 $("#helper-projectType").textContent = q[0] ?? "";
 $("#helper-species").textContent = q[1] ?? "";
 $("#helper-finish").textContent = q[2] ?? "";
}

async function waitForClean(rawKey: string): Promise<string> {
 for (let i = 0; i < CLEAN_WAIT_ATTEMPTS; i++) {
 const res = await fetch(
 `${apiUrl("/api/image-status")}?s3Key=${encodeURIComponent(rawKey)}`,
 );
 const data = await readJsonBody<{
 ready?: boolean;
 cleanKey?: string;
 error?: string;
 }>(res);
 if (res.ok && data.ready && data.cleanKey) return data.cleanKey;
 await new Promise((r) => setTimeout(r, 1000));
 }
 throw new Error(
 "Your photo is still processing. Please wait a moment and try again.",
 );
}

async function uploadFiles(files: File[]): Promise<string[]> {
 const keys: string[] = [];
 for (const file of files) {
 const type = normalizeImageType(file);
 if (!type) {
 throw new Error(
 /\.hei[cf]$/i.test(file.name) || /heic|heif/i.test(file.type)
 ? heicRejectedMessage()
 : "Only JPEG, PNG, and WebP are accepted.",
 );
 }
 const maxBytes = 10 * 1024 * 1024;
 if (file.size < 1 || file.size > maxBytes) {
 throw new Error("Each photo must be 10MB or smaller.");
 }
 const qs = new URLSearchParams({
 filename: file.name,
 type,
 size: String(file.size),
 });
 const res = await fetch(`${apiUrl("/api/upload-url")}?${qs.toString()}`);
 const data = await readJsonBody<{
 ok?: boolean;
 uploadUrl?: string;
 s3Key?: string;
 error?: string;
 }>(res);
 if (!res.ok || !data.uploadUrl || !data.s3Key) {
 throw new Error(data.error ?? "Could not start upload.");
 }
 const put = await fetch(data.uploadUrl, {
 method: "PUT",
 headers: { "content-type": type },
 body: file,
 });
 if (!put.ok) throw new Error("Upload failed. Please try again.");
 keys.push(data.s3Key);
 }
 return keys;
}

async function analyzeFirst(cleanKey: string): Promise<Analysis> {
 const res = await fetch(apiUrl("/api/analyze-image"), {
 method: "POST",
 headers: { "content-type": "application/json" },
 body: JSON.stringify({ s3Key: cleanKey }),
 });
 const data = await readJsonBody<{
 ok?: boolean;
 analysis?: Analysis;
 error?: string;
 }>(res);
 if (!res.ok || !data.analysis) {
 throw new Error(data.error ?? "Analysis failed.");
 }
 return data.analysis;
}

async function runImagePipeline(rawKeys: string[]): Promise<void> {
 showPanel("processing");
 $("#processing-message").textContent = "Reviewing your photo…";
 state.rawKeys = rawKeys;
 const cleanKeys: string[] = [];
 for (const key of rawKeys) {
 cleanKeys.push(await waitForClean(key));
 }
 state.cleanKeys = cleanKeys;

 $("#processing-message").textContent = "Reading the details…";
 const analysis = await analyzeFirst(cleanKeys[0]);
 state.analysis = analysis;
 applyAnalysisToForm(analysis);

 $("#readback-text").textContent = analysis.image_read_back;
 renderReadbackQuestions(analysis.clarifying_questions ?? []);

 if (state.previewUrl) {
 $("#readback-thumb").hidden = false;
 ($("#readback-img") as HTMLImageElement).src = state.previewUrl;
 }

 showPanel("readback");
}

function renderReadbackQuestions(questions: string[]): void {
 const wrap = $("#readback-qa");
 const list = $("#readback-questions");
 list.innerHTML = "";
 const qs = questions.map((q) => q.trim()).filter(Boolean).slice(0, 4);
 if (qs.length === 0) {
 wrap.hidden = true;
 return;
 }
 wrap.hidden = false;
 qs.forEach((question, idx) => {
 const item = document.createElement("div");
 item.className = "readback-qa__item";
 const id = `clarify-answer-${idx}`;
 const label = document.createElement("label");
 label.htmlFor = id;
 label.textContent = question;
 const area = document.createElement("textarea");
 area.id = id;
 area.name = id;
 area.rows = 2;
 area.dataset.question = question;
 area.placeholder = "Your answer (optional)";
 area.setAttribute("aria-label", `Answer: ${question}`);
 item.append(label, area);
 list.appendChild(item);
 });
}

function collectReadbackAnswers(): string {
 const areas = document.querySelectorAll<HTMLTextAreaElement>(
 "#readback-questions textarea[data-question]",
 );
 const parts: string[] = [];
 areas.forEach((area) => {
 const answer = area.value.trim();
 if (!answer) return;
 const question = area.dataset.question ?? "Question";
 parts.push(`Q: ${question}\nA: ${answer}`);
 });
 return parts.join("\n\n");
}

function readFormIntoState(): void {
 const form = $("#details-form") as HTMLFormElement;
 const data = new FormData(form);
 state.form = {
 ...state.form,
 name: String(data.get("name") ?? "").trim(),
 email: String(data.get("email") ?? "").trim(),
 phone: String(data.get("phone") ?? "").trim(),
 projectType: String(data.get("projectType") ?? ""),
 widthIn: String(data.get("widthIn") ?? "").trim(),
 depthIn: String(data.get("depthIn") ?? "").trim(),
 heightIn: String(data.get("heightIn") ?? "").trim(),
 species: String(data.get("species") ?? ""),
 finish: String(data.get("finish") ?? ""),
 timeline: String(data.get("timeline") ?? ""),
 budget: String(data.get("budget") ?? ""),
 notes: String(data.get("notes") ?? "").trim(),
 clarifyingAnswers: state.form.clarifyingAnswers,
 };
}

function validateDetails(): string | null {
 const f = state.form;
 if (f.name.length < 2) return "Please enter your name.";
 if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) return "Please enter a valid email.";
 if (!f.projectType || !f.species || !f.finish || !f.timeline || !f.budget) {
 return "Please complete all required fields.";
 }
 if (f.fixedSpace && (!f.widthIn || !f.depthIn || !f.heightIn)) {
 return "Please enter width, depth, and height for a fixed-space project.";
 }
 return null;
}

function renderReview(): void {
 const f = state.form;
 const size = f.fixedSpace
 ? `${f.widthIn}" W × ${f.depthIn}" D × ${f.heightIn}" H`
 : "Standard proportions";

 const rows: Array<[string, string]> = [
 ["Name", f.name],
 ["Email", f.email],
 ["Phone", f.phone || "None"],
 ["Project", labelFor("projectType", f.projectType)],
 ["Size", size],
 ["Species", labelFor("species", f.species)],
 ["Finish", labelFor("finish", f.finish)],
 ["Timeline", labelFor("timeline", f.timeline)],
 ["Budget", labelFor("budget", f.budget)],
 ["Notes", f.notes || "None"],
 ["Clarifying", f.clarifyingAnswers || "None"],
 ];

 const list = $("#review-list");
 list.innerHTML = "";
 for (const [label, value] of rows) {
 const li = document.createElement("li");
 li.innerHTML = `<span>${label}</span><span></span>`;
 (li.children[1] as HTMLElement).textContent = value;
 list.appendChild(li);
 }

 if (state.previewUrl) {
 $("#confirm-thumb").hidden = false;
 ($("#confirm-img") as HTMLImageElement).src = state.previewUrl;
 } else {
 $("#confirm-thumb").hidden = true;
 }
}

async function submitIntake(): Promise<void> {
 const f = state.form;
 const payload = {
 name: f.name,
 email: f.email,
 phone: f.phone,
 source: state.mode,
 projectType: f.projectType,
 projectTypeLabel: labelFor("projectType", f.projectType),
 fixedSpace: f.fixedSpace,
 widthIn: f.widthIn,
 depthIn: f.depthIn,
 heightIn: f.heightIn,
 species: f.species,
 speciesLabel: labelFor("species", f.species),
 finish: f.finish,
 finishLabel: labelFor("finish", f.finish),
 timeline: f.timeline,
 timelineLabel: labelFor("timeline", f.timeline),
 budget: f.budget,
 budgetLabel: labelFor("budget", f.budget),
 notes: f.notes,
 clarifyingAnswers: f.clarifyingAnswers,
 cleanKeys: state.cleanKeys,
 };

 const res = await fetch(apiUrl("/api/intake"), {
 method: "POST",
 headers: { "content-type": "application/json" },
 body: JSON.stringify(payload),
 });
 const data = await readJsonBody<{
 ok?: boolean;
 ballpark_low?: number;
 ballpark_high?: number;
 project_summary?: string;
 error?: string;
 }>(res);
 if (!res.ok || !data.ok) {
 throw new Error(data.error ?? "Submit failed.");
 }

 const low = Number(data.ballpark_low ?? 0).toLocaleString();
 const high = Number(data.ballpark_high ?? 0).toLocaleString();
 $("#ballpark-range").textContent = `$${low} - $${high}`;
 $("#ballpark-summary").textContent = data.project_summary ?? "";
 showPanel("result");
}

function resetFlow(): void {
 if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
 state.mode = null;
 state.files = [];
 state.previewUrl = "";
 state.rawKeys = [];
 state.cleanKeys = [];
 state.analysis = null;
 state.form = blankForm();
 ($("#photo-input") as HTMLInputElement).value = "";
 ($("#link-input") as HTMLInputElement).value = "";
 ($("#details-form") as HTMLFormElement).reset();
 $("#photo-file-list").innerHTML = "";
 ($("#photo-continue") as HTMLButtonElement).disabled = true;
 setFixedSpace(false);
 populateSelects();
 showPanel("start");
}

function wireEvents(): void {
 document.querySelectorAll<HTMLButtonElement>("[data-start]").forEach((btn) => {
 btn.addEventListener("click", () => {
 const mode = btn.dataset.start as StartMode;
 state.mode = mode;
 if (mode === "photo") showPanel("photo");
 else if (mode === "link") showPanel("link");
 else {
 state.analysis = null;
 populateSelects();
 showPanel("details");
 }
 });
 });

 document.querySelectorAll<HTMLButtonElement>("[data-back]").forEach((btn) => {
 btn.addEventListener("click", () => {
 const target = (btn.dataset.back ?? "start") as Panel;
 showPanel(target);
 });
 });

 $("#photo-input").addEventListener("change", () => {
 const input = $("#photo-input") as HTMLInputElement;
 const files = Array.from(input.files ?? []).slice(0, MAX_FILES);
 const list = $("#photo-file-list");
 list.innerHTML = "";
 setIntakeStatus("#photo-status", "", "idle");

 for (const file of files) {
 const type = normalizeImageType(file);
 if (!type) {
 setIntakeStatus(
 "#photo-status",
 /\.hei[cf]$/i.test(file.name) || /heic|heif/i.test(file.type)
 ? heicRejectedMessage()
 : "Only JPEG, PNG, and WebP are accepted.",
 "error",
 );
 ($("#photo-continue") as HTMLButtonElement).disabled = true;
 return;
 }
 if (file.size > MAX_BYTES) {
 setIntakeStatus("#photo-status", "Each image must be 10MB or smaller.", "error");
 ($("#photo-continue") as HTMLButtonElement).disabled = true;
 return;
 }
 const li = document.createElement("li");
 li.textContent = `${file.name} (${Math.round(file.size / 1024)} KB)`;
 list.appendChild(li);
 }

 state.files = files;
 if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
 state.previewUrl = files[0] ? URL.createObjectURL(files[0]) : "";
 ($("#photo-continue") as HTMLButtonElement).disabled = files.length === 0;
 });

 $("#photo-continue").addEventListener("click", async () => {
 try {
 if (!state.apiConfigured) {
 setIntakeStatus(
 "#photo-status",
 "API is not configured yet. Deploy the stack, then try again.",
 "error",
 );
 return;
 }
 setIntakeStatus("#photo-status", "Uploading…", "loading");
 showPanel("processing");
 $("#processing-message").textContent = "Uploading your photo…";
 const keys = await uploadFiles(state.files);
 await runImagePipeline(keys);
 } catch (err) {
 console.error(err);
 showPanel("photo");
 setIntakeStatus(
 "#photo-status",
 err instanceof Error ? err.message : "Upload failed.",
 "error",
 );
 }
 });

 $("#link-continue").addEventListener("click", async () => {
 const url = ($("#link-input") as HTMLInputElement).value.trim();
 if (!/^https?:\/\//i.test(url)) {
 setIntakeStatus("#link-status", "Enter a full http(s) URL.", "error");
 return;
 }
 try {
 if (!state.apiConfigured) {
 setIntakeStatus(
 "#link-status",
 "API is not configured yet. Deploy the stack, then try again.",
 "error",
 );
 return;
 }
 showPanel("processing");
 $("#processing-message").textContent = "Fetching your image…";
 const res = await fetch(apiUrl("/api/resolve-link"), {
 method: "POST",
 headers: { "content-type": "application/json" },
 body: JSON.stringify({ url }),
 });
 const data = await readJsonBody<{
 ok?: boolean;
 s3Key?: string;
 error?: string;
 }>(res);
 if (!res.ok || !data.s3Key) {
 throw new Error(
 data.error ??
 "We couldn't pull an image from that link. Try uploading the photo directly instead.",
 );
 }
 state.previewUrl = "";
 await runImagePipeline([data.s3Key]);
 } catch (err) {
 console.error(err);
 showPanel("link");
 setIntakeStatus(
 "#link-status",
 err instanceof Error
 ? err.message
 : "We couldn't pull an image from that link. Try uploading the photo directly instead.",
 "error",
 );
 }
 });

 $("#readback-continue").addEventListener("click", () => {
 state.form.clarifyingAnswers = collectReadbackAnswers();
 showPanel("details");
 });

 $("#space-standard").addEventListener("click", () => setFixedSpace(false));
 $("#space-fixed").addEventListener("click", () => setFixedSpace(true));

 $("#details-form").addEventListener("submit", (ev) => {
 ev.preventDefault();
 readFormIntoState();
 const err = validateDetails();
 if (err) {
 setIntakeStatus("#details-status", err, "error");
 return;
 }
 setIntakeStatus("#details-status", "", "idle");
 renderReview();
 showPanel("confirm");
 });

 $("#confirm-edit").addEventListener("click", () => showPanel("details"));

 $("#confirm-submit").addEventListener("click", async () => {
 try {
 setIntakeStatus("#confirm-status", "Estimating your project…", "loading");
 showPanel("processing");
 $("#processing-message").textContent = "Estimating your project…";
 await submitIntake();
 } catch (err) {
 console.error(err);
 showPanel("confirm");
 setIntakeStatus(
 "#confirm-status",
 err instanceof Error ? err.message : "Something went wrong.",
 "error",
 );
 }
 });

 $("#result-reset").addEventListener("click", () => resetFlow());
}

async function initIntake(): Promise<void> {
 await loadIntakeConfig();
 populateSelects();
 setFixedSpace(false);
 wireEvents();
 showPanel("start");
}

if (document.readyState === "loading") {
 document.addEventListener("DOMContentLoaded", () => {
 void initIntake();
 });
} else {
 void initIntake();
}
